/**
 * Shop Not Found Error Component
 * Follows Polaris error message best practices:
 * - Clear explanation of what went wrong
 * - Specific actionable next steps
 * - No technical jargon or overapologizing
 * - Purposeful use of color (critical tone)
 * - Clear visual hierarchy
 */

import { Card, BlockStack, Text, Button, Box, Banner, InlineStack } from '@shopify/polaris';
import { useSearchParams } from 'react-router';

interface ShopNotFoundProps {
  shopDomain: string;
}

export function ShopNotFound({ shopDomain }: ShopNotFoundProps) {
  const [searchParams] = useSearchParams();

  // Preserve all query params for reinstall link
  const queryString = searchParams.toString();

  // Reinstall handler - needs to break out of iframe and redirect to OAuth
  const handleReinstall = () => {
    const reinstallUrl = `/auth?shop=${encodeURIComponent(shopDomain)}${queryString ? `&${queryString}` : ''}`;

    // Break out of Shopify iframe and redirect to OAuth begin
    if (window.top) {
      window.top.location.href = reinstallUrl;
    } else {
      window.location.href = reinstallUrl;
    }
  };

  // Uninstall handler - redirects to Shopify admin apps page
  const handleUninstall = () => {
    // Break out of iframe and redirect to Shopify admin apps page
    const shopifyAdminAppsUrl = `https://${shopDomain}/admin/settings/apps`;

    if (window.top) {
      window.top.location.href = shopifyAdminAppsUrl;
    } else {
      window.location.href = shopifyAdminAppsUrl;
    }
  };

  return (
    <Box paddingBlockStart="800" paddingInline="400">
      {/* Use Polaris layout: centered, max-width for readability */}
      <Box maxWidth="560px" paddingInline="0">
        <BlockStack gap="400">
          {/* Critical Banner - Polaris pattern for errors */}
          <Banner
            title="Store data not found"
            tone="critical"
          >
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd">
                <Text as="span" fontWeight="bold">{shopDomain}</Text> is not in our system.
              </Text>
              <Text as="p" variant="bodyMd">
                This happens when the app was uninstalled, data was deleted, or installation didn't complete.
              </Text>
            </BlockStack>
          </Banner>

          {/* Action Card - Primary focus */}
          <Card>
            <BlockStack gap="400">

              {/* Primary Action - Polaris pattern: most important action first */}
              <BlockStack gap="300">
                <BlockStack gap="200">
                  <Text as="p" variant="headingSm">
                    Set up Stock Sentry again
                  </Text>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    Reinstall the app with fresh data and start monitoring your inventory.
                  </Text>
                  <Box paddingBlockStart="200">
                    <Button
                      variant="primary"
                      onClick={handleReinstall}
                      size="large"
                    >
                      Reinstall app
                    </Button>
                  </Box>
                </BlockStack>

                {/* Divider for visual separation */}
                <Box
                  paddingBlockStart="200"
                  paddingBlockEnd="200"
                  borderBlockStartWidth="025"
                  borderColor="border"
                />

                {/* Secondary Action - Less emphasis */}
                <BlockStack gap="200">
                  <Text as="p" variant="headingSm">
                    Remove Stock Sentry
                  </Text>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    Uninstall the app from your Shopify admin.
                  </Text>
                  <Box paddingBlockStart="200">
                    <Button
                      onClick={handleUninstall}
                    >
                      Go to apps settings
                    </Button>
                  </Box>
                </BlockStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </BlockStack>
      </Box>
    </Box>
  );
}
